<template>
  <date-picker
    type="datetime"
    compact-time
    v-model="startDate"
    class="custom-date-picker"
    :format="'jYYYY-jMM-jDD hh:mm'"
    :displayFormat="'jYYYY-jMM-jDD hh:mm'"
    custom-input=".start-date-input"
  />
  <date-picker
    type="datetime"
    compact-time
    v-model="endDate"
    class="custom-date-picker"
    :format="'jYYYY-jMM-jDD hh:mm'"
    :displayFormat="'jYYYY-jMM-jDD hh:mm'"
    custom-input=".end-date-input"
  />
  <div
    class="flex flex-col items-center px-5 gap-4 max-h-[28rem] overflow-auto"
  >
    <div class="w-full flex flex-col gap-2">
      <label for="reminderTitle" class="w-full text-right mr-2"
        >تیتر رویداد</label
      >
      <input
        type="text"
        name="reminderTitle"
        class="input w-full"
        v-model="title"
      />
      <span v-if="errors.title" class="text-red-500 text-sm">{{
        errors.title
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2 mt-4 pb-4">
      <label for="eventColor" class="w-full text-right mr-2">رنگ رویداد</label>
      <div class="flex gap-4 text-stone-500">
        <label
          ><input
            type="radio"
            name="eventColor"
            value="danger"
            v-model="color"
          />
          قرمز</label
        >
        <label
          ><input
            type="radio"
            name="eventColor"
            value="success"
            v-model="color"
          />
          سبز</label
        >
        <label
          ><input
            type="radio"
            name="eventColor"
            value="primary"
            v-model="color"
          />
          آبی</label
        >
        <label
          ><input
            type="radio"
            name="eventColor"
            value="warning"
            v-model="color"
          />
          زرد</label
        >
      </div>
      <span v-if="errors.color" class="text-red-500 text-sm">{{
        errors.color
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2">
      <label for="startDate" class="w-full text-right mr-2"
        >تاریخ شروع رویداد</label
      >
      <input
        type="text"
        class="start-date-input input w-full font-vazir-thin"
        :value="startDate"
      />
      <span v-if="errors.startDate" class="text-red-500 text-sm">{{
        errors.startDate
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2">
      <label for="endDate" class="w-full text-right mr-2"
        >تاریخ پایان رویداد</label
      >
      <input
        type="text"
        class="end-date-input input w-full font-vazir-thin"
        :value="endDate"
      />
      <span v-if="errors.endDate" class="text-red-500 text-sm">{{
        errors.endDate
      }}</span>
    </div>

    <div class="mt-4 w-full flex justify-end items-center gap-4">
      <button
        @click="closeModal"
        class="text-stone-500 rounded-lg border border-transparent transition-all active:border-black py-2 px-4 mobile:text-sm"
      >
        بستن
      </button>
      <button
        @click="deleteReminderHandler"
        class="bg-red-100 text-red-500 rounded-lg py-2 px-4 transition-colors hover:bg-red-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center mobile:text-sm"
      >
        حذف رویداد
      </button>
      <button
        @click="handleSubmit"
        class="bg-green-500 text-white rounded-lg py-2 px-4 transition-colors hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center mobile:text-sm"
        :disabled="postMutation.isLoading"
      >
        بروزرسانی تغییرات
      </button>
    </div>
  </div>
</template>

<script setup>
import moment from "jalali-moment";
import { computed, ref } from "vue";
import { defineEmits } from "vue";
import DatePicker from "vue3-persian-datetime-picker";
import { useMutation } from "@tanstack/vue-query";
import {
  updateReminder,
  deleteReminder,
} from "@/services/api/apiServiceReminders";
import { useStore } from "vuex";
import { convertToJalali } from "@/services/helpers";
import Swal from "sweetalert2";

const store = useStore();
const selectedReminder = computed(
  () => store.getters["remindersSlice/selectedReminder"],
);
const id = selectedReminder.value.id;
const title = ref(selectedReminder.value.title);
const color = ref("danger");
const startDate = ref(convertToJalali(selectedReminder.value.start));
const endDate = ref(convertToJalali(selectedReminder.value.end));
const errors = ref({
  title: "",
  color: "",
  startDate: "",
  endDate: "",
});
const emit = defineEmits(["close"]);
// Mutation for updating reminders
const postMutation = useMutation({
  mutationFn: updateReminder,
  onSuccess: (data) => {
    store.commit("remindersSlice/updateReminder", data);
    Swal.fire({
      icon: "success",
      title: "رویداد با موفقیت بروزرسانی شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    closeModal();
  },
  onError: (error) => {
    console.error("Error posting reminder:", error);
    Swal.fire({
      icon: "error",
      title: "ویرایش رویداد با خطا مواجه شد!",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

// Mutation for deleting reminders
const deleteMutation = useMutation({
  mutationFn: deleteReminder,
  onSuccess: () => {
    store.commit("remindersSlice/deleteReminder", id);
    Swal.fire({
      icon: "success",
      title: "رویداد با موفقیت حذف شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    closeModal();
  },
  onError: (error) => {
    console.error("Error deleting reminder:", error);
    Swal.fire({
      icon: "error",
      title: "حذف رویداد با خطا مواجه شد!",
      showConfirmButton: false,
      timer: 1500,
    });
  },
});

function validateForm() {
  let isValid = true;
  errors.value = { title: "", color: "", startDate: "", endDate: "" };

  if (!title.value.trim()) {
    errors.value.title = "تیتر رویداد اجباری است.";
    isValid = false;
  }
  if (!color.value) {
    errors.value.color = "انتخاب رنگ رویداد اجباری است.";
    isValid = false;
  }
  if (!startDate.value) {
    errors.value.startDate = "تاریخ شروع رویداد اجباری است.";
    isValid = false;
  }
  if (!endDate.value) {
    errors.value.endDate = "تاریخ پایان رویداد اجباری است.";
    isValid = false;
  } else if (
    moment(endDate.value, "jYYYY-jMM-jDD HH:mm").isBefore(
      moment(startDate.value, "jYYYY-jMM-jDD HH:mm"),
    )
  ) {
    errors.value.endDate = "تاریخ پایان باید بعد از تاریخ شروع باشد.";
    isValid = false;
  }

  return isValid;
}

function handleSubmit() {
  if (!validateForm()) return;

  const reminderObj = {
    id: +selectedReminder.value.id,
    user_id: selectedReminder.value.extendedProps.user_id,
    title: title.value,
    color: color.value,
    start_date: moment(startDate.value, "jYYYY-jMM-jDD").format(
      "YYYY-MM-DD hh:mm:ss",
    ),
    end_date: moment(endDate.value, "jYYYY-jMM-jDD").format(
      "YYYY-MM-DD hh:mm:ss",
    ),
  };

  postMutation.mutate({ id, updatedData: reminderObj });
}

function deleteReminderHandler() {
  Swal.fire({
    title: "آیا از حذف این رویداد اطمینان دارید؟",
    text: `رویداد "${title.value}" حذف خواهد شد.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "بله، حذف شود!",
    cancelButtonText: "لغو",
  }).then((result) => {
    if (result.isConfirmed) deleteMutation.mutate(id);
  });
}

function closeModal() {
  emit("close");
}
</script>

<style>
.swal2-container {
  @apply font-vazirmatn z-[99999999999];
}

.swal2-title {
  font-size: 1.5rem;
  font-weight: bold;
  @apply dark:text-neutral-100;
}

.swal2-html-container {
  font-size: 1rem;
  line-height: 1.5;
  @apply dark:text-neutral-200;
}
.swal2-popup {
  @apply dark:bg-neutral-700 !important;
}
</style>
